import { useState, useEffect } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Phone, Mail, Clock, Facebook, Youtube, Instagram, Heart, Sparkles } from 'lucide-react'
import { useSite } from '../../context/SiteContext'
import { cn } from '../../utils/helpers'

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Ministries', path: '/ministries' },
  { name: 'Sermons', path: '/sermons' },
  { name: 'Events', path: '/events' },
  { name: 'Blog', path: '/blog' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'Contact', path: '/contact' },
]

const Header = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()
  const { settings } = useSite()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsOpen(false)
  }, [location])

  return (
    <>
      {/* Top Bar - Slim and Modern */}
      <div className="hidden lg:block bg-gradient-to-r from-dark-100 to-dark-200 text-white text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-6">
              <a
                href={`tel:${settings.phone}`}
                className="flex items-center gap-2 hover:text-primary-400 transition-colors"
              >
                <Phone className="w-3.5 h-3.5" />
                <span className="font-medium">{settings.phone}</span>
              </a>
              <span className="w-px h-4 bg-white/20" />
              <a
                href={`mailto:${settings.email}`}
                className="flex items-center gap-2 hover:text-primary-400 transition-colors"
              >
                <Mail className="w-3.5 h-3.5" />
                <span className="font-medium">{settings.email}</span>
              </a>
              <span className="w-px h-4 bg-white/20" />
              <span className="flex items-center gap-2 text-primary-300">
                <Clock className="w-3.5 h-3.5" />
                <span className="font-medium">Sunday Worship: 9:00 AM</span>
              </span>
              <span className="w-px h-4 bg-white/20" />
              <Link
                to="/plan-your-visit"
                className="flex items-center gap-2 bg-gradient-to-r from-accent-500 to-accent-600 hover:from-accent-600 hover:to-accent-700 text-white px-4 py-1.5 rounded-full font-semibold text-xs transition-all hover:scale-105 shadow-lg shadow-accent-500/30"
              >
                Plan Your Visit
              </Link>
            </div>

            <div className="flex items-center gap-4">
              {/* Donate Button - Bold & Visible */}
              <motion.a
                href="https://graceledger-production.up.railway.app/give"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-gradient-to-r from-red-500 via-secondary-500 to-red-500 bg-[length:200%_100%] animate-gradient text-white px-6 py-2 rounded-full font-bold text-sm shadow-xl shadow-red-500/40 border-2 border-white/30"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                animate={{
                  boxShadow: [
                    '0 0 0 0 rgba(239, 68, 68, 0.5)',
                    '0 0 0 8px rgba(239, 68, 68, 0)',
                    '0 0 0 0 rgba(239, 68, 68, 0)'
                  ]
                }}
                transition={{
                  boxShadow: { duration: 1.5, repeat: Infinity },
                  scale: { duration: 0.2 }
                }}
              >
                <Heart className="w-4 h-4 fill-current animate-pulse" />
                GIVE NOW
              </motion.a>

              <span className="w-px h-4 bg-white/20" />

              <div className="flex items-center gap-3">
                {settings.socialMedia?.facebook && (
                  <a
                    href={settings.socialMedia.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary-400 transition-colors hover:scale-110 transform"
                  >
                    <Facebook className="w-4 h-4" />
                  </a>
                )}
                {settings.socialMedia?.youtube && (
                  <a
                    href={settings.socialMedia.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-red-400 transition-colors hover:scale-110 transform"
                  >
                    <Youtube className="w-4 h-4" />
                  </a>
                )}
                {settings.socialMedia?.instagram && (
                  <a
                    href={settings.socialMedia.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-pink-400 transition-colors hover:scale-110 transform"
                  >
                    <Instagram className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={cn(
          'sticky top-0 z-50 transition-all duration-500',
          isScrolled
            ? 'bg-white/80 backdrop-blur-xl shadow-soft py-2'
            : 'bg-white py-3'
        )}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <motion.div
                whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                transition={{ duration: 0.5 }}
              >
                <img
                  src="/images/logo.png"
                  alt="DLBC Clarksville TN"
                  className="h-14 w-auto transition-transform group-hover:scale-105"
                />
              </motion.div>
              <div className="hidden sm:block">
                <span className="font-heading font-bold text-lg bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                  DLBC Clarksville TN
                </span>
                <span className="block text-xs font-accent text-dark-300 flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-accent-500" />
                  A Disciple Making Church
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1 bg-light-100 rounded-full p-1.5">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    cn(
                      'px-5 py-2.5 rounded-full font-accent font-medium text-sm transition-all duration-300',
                      isActive
                        ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg shadow-primary-500/25'
                        : 'text-dark-300 hover:text-primary-600 hover:bg-white'
                    )
                  }
                >
                  {link.name}
                </NavLink>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-3">
              <Link to="/new-here">
                <motion.button
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.95 }}
                  animate={{
                    boxShadow: ['0 0 0 0 rgba(124, 58, 237, 0.4)', '0 0 0 10px rgba(124, 58, 237, 0)', '0 0 0 0 rgba(124, 58, 237, 0)']
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 1
                  }}
                  className="px-6 py-3 rounded-full font-accent font-bold text-base bg-gradient-to-r from-primary-600 via-secondary-500 to-primary-600 bg-[length:200%_100%] animate-gradient text-white shadow-xl shadow-primary-500/40 hover:shadow-2xl hover:shadow-secondary-500/50 transition-all duration-300 border-2 border-white/20"
                >
                  I'm New Here! 👋
                </motion.button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2.5 rounded-2xl bg-light-100 text-dark-300 hover:bg-primary-100 hover:text-primary-600 transition-colors"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </motion.button>
          </div>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden bg-white border-t border-light-200"
            >
              <div className="px-4 py-6 space-y-2">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <NavLink
                      to={link.path}
                      className={({ isActive }) =>
                        cn(
                          'block px-5 py-3.5 rounded-2xl font-accent font-medium transition-all duration-300',
                          isActive
                            ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white'
                            : 'text-dark-300 hover:bg-light-100'
                        )
                      }
                    >
                      {link.name}
                    </NavLink>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="pt-4 space-y-3"
                >
                  <Link to="/plan-your-visit" className="block">
                    <button className="w-full px-5 py-3.5 rounded-2xl font-accent font-semibold bg-gradient-to-r from-accent-500 to-accent-600 text-white shadow-lg">
                      Plan Your Visit
                    </button>
                  </Link>
                  <Link to="/new-here" className="block">
                    <button className="w-full px-5 py-3.5 rounded-2xl font-accent font-semibold border-2 border-primary-500 text-primary-600 hover:bg-primary-500 hover:text-white transition-all">
                      I'm New Here!
                    </button>
                  </Link>
                  <a
                    href="https://graceledger-production.up.railway.app/give"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-2xl font-accent font-semibold bg-gradient-to-r from-secondary-500 to-secondary-600 text-white shadow-lg"
                  >
                    <Heart className="w-4 h-4 fill-current" />
                    Give Online
                  </a>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  )
}

export default Header
